import { z } from "zod";
export const BLOCKING_CLASSES = [
    "uncertified_drafts",
    "unresolved_user_decisions",
    "unanswered_rfis",
    "unapproved_assumptions",
    "unchecked_takeoff_anomalies",
    "missing_catalogue_prices",
    "failed_document_reads",
    "stale_generation",
    "unreported_ai_usage",
    "unresolved_quote_lines",
    "incomplete_downstream_rows"
];
export const BlockingClassSchema = z.enum(BLOCKING_CLASSES);
export const LEDGER_RECORD_TYPES = ["decision", "schedule", "issue"];
export const LedgerRecordTypeSchema = z.enum(LEDGER_RECORD_TYPES);
export const LEDGER_KINDS = [
    "risk",
    "decision",
    "rfi",
    "clarification",
    "assumption",
    "exclusion",
    "follow_up",
    "takeoff_anomaly",
    "catalogue_price_gap",
    "document_read_failure",
    "generation_stale",
    "ai_usage_gap",
    "quote_line_issue",
    "downstream_completeness_issue"
];
export const LedgerKindSchema = z.enum(LEDGER_KINDS);
export const DECISION_AUDIENCES = [
    "internal_product_policy",
    "estimator_decision",
    "technical_design_review",
    "client_rfi"
];
export const DecisionAudienceSchema = z.enum(DECISION_AUDIENCES);
export const LEDGER_STATES = [
    "proposed",
    "reviewed",
    "accepted",
    "retired",
    "exported"
];
export const LedgerStateSchema = z.enum(LEDGER_STATES);
export const PUBLICATION_STATES = [
    "internal_only",
    "client_candidate",
    "client_approved"
];
export const PublicationStateSchema = z.enum(PUBLICATION_STATES);
export const GATE_POLICIES = ["hard", "waivable", "advisory"];
export const GatePolicySchema = z.enum(GATE_POLICIES);
export const LEDGER_RESOLUTION_OUTCOMES = [
    "choice_made",
    "answer_accepted",
    "assumption_approved",
    "source_corrected",
    "false_positive",
    "not_applicable",
    "waived"
];
export const LedgerResolutionOutcomeSchema = z.enum(LEDGER_RESOLUTION_OUTCOMES);
export const Sha256Schema = z.string().regex(/^sha256:[0-9a-f]{64}$/);
const NonEmptyStringSchema = z.string().trim().min(1);
const TimestampSchema = z.string().datetime({ offset: true });
export const LedgerHistoryEventV1Schema = z
    .object({
    id: NonEmptyStringSchema,
    entryId: NonEmptyStringSchema,
    entryRevision: z.number().int().positive(),
    action: NonEmptyStringSchema,
    fromState: LedgerStateSchema.nullable(),
    toState: LedgerStateSchema,
    actorId: NonEmptyStringSchema,
    occurredAt: TimestampSchema,
    reasonCode: NonEmptyStringSchema.nullable(),
    reason: NonEmptyStringSchema.nullable(),
    sourceVersionHash: Sha256Schema
})
    .strict();
export const LedgerWaiverV1Schema = z
    .object({
    status: z.enum(["active", "revoked"]),
    reason: NonEmptyStringSchema,
    authorId: NonEmptyStringSchema,
    createdAt: TimestampSchema,
    sourceRevision: z.number().int().positive(),
    revokedBy: NonEmptyStringSchema.nullable(),
    revokedAt: TimestampSchema.nullable()
})
    .strict()
    .superRefine((waiver, context) => {
    const revokedFieldsPresent = waiver.revokedBy !== null && waiver.revokedAt !== null;
    if (waiver.status === "active" && (waiver.revokedBy || waiver.revokedAt)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "An active waiver cannot contain revocation metadata."
        });
    }
    if (waiver.status === "revoked" && !revokedFieldsPresent) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A revoked waiver requires revocation actor and timestamp."
        });
    }
});
export const ManualPriceOverrideV1Schema = z
    .object({
    amount: z.number().nonnegative(),
    currency: z.literal("GBP"),
    reason: NonEmptyStringSchema,
    authorId: NonEmptyStringSchema,
    createdAt: TimestampSchema,
    sourceRevision: z.number().int().positive()
})
    .strict();
export const ProjectLedgerEntryV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    id: NonEmptyStringSchema,
    orgId: NonEmptyStringSchema,
    projectId: NonEmptyStringSchema,
    revision: z.number().int().positive(),
    isCurrent: z.boolean(),
    recordType: LedgerRecordTypeSchema,
    kind: LedgerKindSchema,
    primaryBlockingClass: BlockingClassSchema.nullable(),
    secondaryTags: z.array(NonEmptyStringSchema),
    audience: DecisionAudienceSchema,
    audienceReviewRequired: z.boolean(),
    state: LedgerStateSchema,
    publicationState: PublicationStateSchema,
    internal: z
        .object({ title: NonEmptyStringSchema, body: z.string().nullable() })
        .strict(),
    clientCopy: z
        .object({
        title: NonEmptyStringSchema,
        body: NonEmptyStringSchema,
        reviewedBy: NonEmptyStringSchema,
        reviewedAt: TimestampSchema,
        sourceRevision: z.number().int().positive()
    })
        .strict()
        .nullable(),
    classification: z
        .object({
        category: NonEmptyStringSchema,
        severity: z.enum(["info", "warning", "blocker"]),
        scope: NonEmptyStringSchema.nullable()
    })
        .strict(),
    sourceLinks: z.array(z
        .object({
        sourceType: NonEmptyStringSchema,
        sourceId: NonEmptyStringSchema,
        sourceRevision: NonEmptyStringSchema.nullable(),
        href: NonEmptyStringSchema.nullable()
    })
        .strict()),
    legacyAliases: z.array(z
        .object({
        sourceType: NonEmptyStringSchema,
        sourceId: NonEmptyStringSchema
    })
        .strict()),
    dedupeKey: NonEmptyStringSchema,
    resolution: z
        .object({
        outcome: LedgerResolutionOutcomeSchema,
        summary: NonEmptyStringSchema,
        acceptedBy: NonEmptyStringSchema,
        acceptedAt: TimestampSchema,
        sourceRevision: z.number().int().positive()
    })
        .strict()
        .nullable(),
    waiver: LedgerWaiverV1Schema.nullable(),
    manualPriceOverride: ManualPriceOverrideV1Schema.nullable().optional(),
    exportDisposition: z
        .object({
        status: z.enum(["not_eligible", "blocked", "included", "excluded", "exported"]),
        reason: z.string().nullable(),
        exportId: NonEmptyStringSchema.nullable(),
        sourceRevision: z.number().int().positive().nullable()
    })
        .strict(),
    promotedFromEntryId: NonEmptyStringSchema.nullable(),
    supersedesEntryId: NonEmptyStringSchema.nullable(),
    createdAt: TimestampSchema,
    createdBy: NonEmptyStringSchema,
    updatedAt: TimestampSchema,
    updatedBy: NonEmptyStringSchema,
    history: z.array(LedgerHistoryEventV1Schema)
})
    .strict()
    .superRefine((entry, context) => {
    if (entry.resolution && entry.waiver?.status === "active") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "An entry cannot have both an accepted resolution and an active waiver."
        });
    }
    if (entry.manualPriceOverride) {
        if (entry.primaryBlockingClass !== "missing_catalogue_prices" ||
            entry.manualPriceOverride.sourceRevision !== entry.revision ||
            entry.resolution?.outcome !== "source_corrected" ||
            entry.resolution.sourceRevision !== entry.revision) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["manualPriceOverride"],
                message: "A manual price override must resolve the current missing-price revision."
            });
        }
    }
    if (entry.primaryBlockingClass !== null &&
        entry.state === "accepted" &&
        entry.resolution === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "An accepted blocker requires an accepted resolution."
        });
    }
    if (entry.audience === "internal_product_policy") {
        if (entry.publicationState !== "internal_only") {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Internal product policy entries must remain internal-only."
            });
        }
        if (["included", "exported"].includes(entry.exportDisposition.status)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Internal product policy entries cannot enter client export."
            });
        }
    }
    if (entry.publicationState === "client_approved") {
        if (!entry.clientCopy || entry.clientCopy.sourceRevision !== entry.revision) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Client approval requires reviewed copy for the current revision."
            });
        }
    }
    if (entry.history.some((event) => event.entryId !== entry.id)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Every history event must belong to the enclosing ledger entry."
        });
    }
});
export const LEDGER_TRANSITION_ACTIONS = [
    "review",
    "accept",
    "retire",
    "export",
    "waive",
    "revoke_waiver",
    "include_in_export",
    "exclude_from_export"
];
export const LedgerTransitionActionSchema = z.enum(LEDGER_TRANSITION_ACTIONS);
export const LedgerTransitionCommandV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    action: LedgerTransitionActionSchema,
    expectedRevision: z.number().int().positive(),
    reason: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((command, context) => {
    if (["retire", "waive", "revoke_waiver", "exclude_from_export"].includes(command.action) &&
        !command.reason) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reason"],
            message: `Transition ${command.action} requires a reason.`
        });
    }
});
export const LedgerPromotionCommandV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    expectedRevision: z.number().int().positive(),
    targetRecordType: LedgerRecordTypeSchema,
    targetKind: LedgerKindSchema,
    targetAudience: DecisionAudienceSchema,
    sourceRevision: z.number().int().positive(),
    clientCopy: z
        .object({ title: NonEmptyStringSchema, body: NonEmptyStringSchema })
        .strict()
})
    .strict()
    .superRefine((command, context) => {
    if (command.targetAudience === "internal_product_policy") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["targetAudience"],
            message: "Promotion must create a separately audience-classified child."
        });
    }
    if (command.targetKind === "rfi" && command.targetAudience !== "client_rfi") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["targetAudience"],
            message: "An RFI promotion must target the client_rfi audience."
        });
    }
});
export const PROJECT_LEDGER_EXPORT_DISPOSITIONS = [
    "not_eligible",
    "blocked",
    "included",
    "excluded",
    "exported"
];
const ledgerRecordTypeCountsShape = Object.fromEntries(LEDGER_RECORD_TYPES.map((value) => [value, z.number().int().nonnegative()]));
const ledgerStateCountsShape = Object.fromEntries(LEDGER_STATES.map((value) => [value, z.number().int().nonnegative()]));
const audienceCountsShape = Object.fromEntries(DECISION_AUDIENCES.map((value) => [value, z.number().int().nonnegative()]));
const publicationCountsShape = Object.fromEntries(PUBLICATION_STATES.map((value) => [value, z.number().int().nonnegative()]));
const exportDispositionCountsShape = Object.fromEntries(PROJECT_LEDGER_EXPORT_DISPOSITIONS.map((value) => [
    value,
    z.number().int().nonnegative()
]));
export const ProjectLedgerPageV1Schema = z
    .object({
    schemaVersion: z.literal(1),
    revision: z.number().int().nonnegative(),
    items: z.array(ProjectLedgerEntryV1Schema),
    pageInfo: z
        .object({
        nextCursor: NonEmptyStringSchema.nullable(),
        hasNextPage: z.boolean()
    })
        .strict(),
    facets: z
        .object({
        uniqueEntryCount: z.number().int().nonnegative(),
        byRecordType: z.object(ledgerRecordTypeCountsShape).strict(),
        byKind: z.record(LedgerKindSchema, z.number().int().nonnegative()),
        byState: z.object(ledgerStateCountsShape).strict(),
        byAudience: z.object(audienceCountsShape).strict(),
        byPublicationState: z.object(publicationCountsShape).strict(),
        byBlockingClass: z.record(BlockingClassSchema, z.number().int().nonnegative()),
        byExportDisposition: z.object(exportDispositionCountsShape).strict()
    })
        .strict()
})
    .strict();
export const LEDGER_ALLOWED_TRANSITIONS = {
    proposed: ["reviewed", "retired"],
    reviewed: ["accepted", "retired"],
    accepted: ["exported", "retired"],
    exported: [],
    retired: []
};
export const isLedgerTransitionAllowed = (from, to) => LEDGER_ALLOWED_TRANSITIONS[from].includes(to);
export const isCurrentLedgerWaiver = (entry) => entry.waiver?.status === "active" &&
    entry.waiver.sourceRevision === entry.revision &&
    entry.resolution === null;
export const canLedgerEntryEnterClientExport = (entry) => entry.audience !== "internal_product_policy" &&
    entry.state === "accepted" &&
    entry.publicationState === "client_approved" &&
    entry.clientCopy !== null &&
    entry.clientCopy.sourceRevision === entry.revision &&
    entry.exportDisposition.status === "included" &&
    (entry.kind !== "rfi" || entry.audience === "client_rfi");
//# sourceMappingURL=projectLedger.js.map